import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { positionsQuery } from '../../hooks/usePositions';
import { useProductsById } from '../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useSubaccountLiquidation } from '../../hooks/useWebsocketStore';
import { invalidateQueries } from '../../lib/reactQueryHelpers';
import { showToast } from '../../lib/showToast';
import { liquidatedToasts } from '../../lib/toasts';
import { SubaccountLiquidation } from '../../types/liquidation';

export const LiquidationTracker = () => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const queryClient = useQueryClient();
  const currentSubaccountId = selectedSubaccount?.id;
  const products = useProductsById();

  const handleLiquidationEvent = useCallback(
    (liquidation: SubaccountLiquidation) => {
      // Invalidate open and closed position queries
      invalidateQueries(
        queryClient,
        positionsQuery({ subaccountId: liquidation.subaccountId, open: true }).queryKey
      );

      const closedPositionsQuery = positionsQuery({
        subaccountId: liquidation.subaccountId,
        open: false,
      });
      invalidateQueries(queryClient, closedPositionsQuery.queryKey);

      // Wait for updated closed positions.
      queryClient
        .fetchInfiniteQuery(closedPositionsQuery)
        .then(() => {
          // Enumerate liquidated positions based on updatedAt timestamp.
          const queryData = queryClient.getQueryData(closedPositionsQuery.queryKey);
          const closedPositions = queryData?.pages?.flatMap((page) => page.data) || [];
          const liquidatedPositions = closedPositions.filter(
            (position) => position.isLiquidated && position.updatedAt >= liquidation.liquidatedAt
          );

          if (liquidatedPositions.length === 0) {
            throw new Error(`No new liquidated positions after liquidation event`);
          }

          liquidatedToasts(liquidatedPositions, products, liquidation.liquidatedAt).forEach(
            (toast) => showToast(toast)
          );
        })
        .catch((error) => {
          console.error(`Failed to identify liquidated positions`, error);
          liquidatedToasts(undefined, products, liquidation.liquidatedAt).forEach((toast) =>
            showToast(toast)
          );
        });
    },
    [products, queryClient]
  );

  // Subscribe to the liquidation event for the current subaccount
  useSubaccountLiquidation(currentSubaccountId, handleLiquidationEvent);

  return null;
};
